/**
 * CSS Ingestion — Layer 3 of NSPP.
 *
 * Parses a small subset of HTML + CSS into scene nodes. This is the
 * "real CSS" layer that turns `<div class="card" style="border-radius: 16px;
 * box-shadow: 0 4px 12px red">` into a SceneNode ready for the SDF pipeline.
 *
 * Supported HTML elements:
 *   div, section, header, footer, nav, main, aside, article
 *   h1-h6, p, span, a, strong, em, code, pre, small, label
 *   button, input, hr
 *   ul, ol, li
 *   img
 *
 * Supported CSS properties (the "real" subset, enough for landing pages):
 *   Layout:     position, top/right/bottom/left, width, height,
 *               min-width/height, max-width/height, margin, padding,
 *               display, flex, flex-direction, flex-wrap, justify-content,
 *               align-items, align-self, flex-grow, flex-shrink, flex-basis,
 *               gap, row-gap, column-gap, box-sizing
 *   Visual:     background, background-color, background-image,
 *               background-size, background-position, background-repeat,
 *               border, border-top/right/bottom/left, border-color,
 *               border-radius, border-top-left-radius etc.,
 *               box-shadow, opacity, visibility, pointer-events, overflow
 *   Text:       color, font-size, font-weight, font-family, font-style,
 *               line-height, letter-spacing, text-align, text-transform,
 *               text-decoration, text-overflow, white-space
 *   Transform:  transform (translate, rotate, scale, matrix),
 *               transform-origin
 *   Filter:     filter (blur, brightness, contrast, saturate, hue-rotate,
 *               invert), backdrop-filter (mapped to filter for PoC)
 *   Compositing: mix-blend-mode, isolation
 *   Animation:  animation, animation-name, animation-duration,
 *               animation-delay, animation-iteration-count,
 *               animation-direction, animation-fill-mode, animation-timing-
 *               function
 *
 * Supported selectors:
 *   tag, .class, tag.class, tag.class:hover, .class:focus, tag:hover,
 *   tag:focus, tag:active
 *
 * NOT supported (explicitly out of scope for this PoC):
 *   - External stylesheets / <link rel="stylesheet">
 *   - @media queries
 *   - CSS variables (var(--name))
 *   - CSS Grid
 *   - @keyframes files (keyframes must be defined in the <style> block of
 *     the HTML; we use a JS-side definition for that)
 *   - Pseudo-elements ::before, ::after
 *   - calc() inside transforms (supported in layout, not in transform)
 *
 * The parser is a hand-rolled, dependency-free tokenizer + recursive descent.
 * Production-grade? No. Reads "real" HTML/CSS? Mostly yes, for the supported
 * subset.
 */

import {
  addNode,
  createScene,
  makeImage,
  makeRect,
  makeText,
  type BorderWidths,
  type ComputedStyle,
  type CornerRadii,
  type FilterChain,
  type KeyframeDef,
  type KeyframeTrack,
  type RGBA,
  type Scene,
  type SceneNode,
} from "./sceneGraph";

// ---------------------------------------------------------------------------
// HTML tokenizer (very small subset)
// ---------------------------------------------------------------------------

interface HtmlToken {
  kind: "open" | "close" | "void" | "text";
  tag: string;
  attrs: Record<string, string>;
  text: string;
}

const VOID_ELEMENTS = new Set([
  "br", "hr", "img", "input", "meta", "link", "area", "base", "col",
  "embed", "param", "source", "track", "wbr",
]);

/** Tokenize an HTML string into a flat list of tokens. */
export function tokenizeHtml(html: string): HtmlToken[] {
  const tokens: HtmlToken[] = [];
  let i = 0;
  while (i < html.length) {
    if (html[i] === "<") {
      // Comment?
      if (html.startsWith("<!--", i)) {
        const end = html.indexOf("-->", i + 4);
        i = end < 0 ? html.length : end + 3;
        continue;
      }
      // Doctype?
      if (html[i + 1] === "!") {
        const end = html.indexOf(">", i);
        i = end < 0 ? html.length : end + 1;
        continue;
      }
      const isClose = html[i + 1] === "/";
      const start = isClose ? i + 2 : i + 1;
      const end = html.indexOf(">", start);
      if (end < 0) {
        i = html.length;
        continue;
      }
      const raw = html.slice(start, end).trim();
      const spaceIdx = raw.search(/\s/);
      const tag = (spaceIdx < 0 ? raw : raw.slice(0, spaceIdx)).toLowerCase();
      const attrPart = spaceIdx < 0 ? "" : raw.slice(spaceIdx + 1);
      const attrs = parseAttrs(attrPart);
      if (isClose) {
        tokens.push({ kind: "close", tag, attrs, text: "" });
        i = end + 1;
      } else if (VOID_ELEMENTS.has(tag)) {
        tokens.push({ kind: "void", tag, attrs, text: "" });
        i = end + 1;
      } else if (tag === "style" || tag === "script") {
        // <style> and <script> contents are NOT text nodes — skip past
        // </style>/</script> so the body (CSS rules or JS code) doesn't get
        // emitted as visible text. CSS rules are picked up separately via
        // extractStyleBlocks(), which scans the raw HTML directly.
        tokens.push({ kind: "open", tag, attrs, text: "" });
        const closeTag = `</${tag}>`;
        const closeAt = html.toLowerCase().indexOf(closeTag, end + 1);
        i = closeAt < 0 ? html.length : closeAt + closeTag.length;
      } else {
        tokens.push({ kind: "open", tag, attrs, text: "" });
        i = end + 1;
      }
    } else {
      // Text
      const next = html.indexOf("<", i);
      const text = html.slice(i, next < 0 ? html.length : next);
      const trimmed = text.trim();
      if (trimmed.length > 0) {
        tokens.push({ kind: "text", tag: "", attrs: {}, text: decodeEntities(trimmed) });
      }
      i = next < 0 ? html.length : next;
    }
  }
  return tokens;
}

function parseAttrs(raw: string): Record<string, string> {
  const out: Record<string, string> = {};
  // Match name="value" / name='value' / name=val / name
  const re = /([a-zA-Z_:][\w:-]*)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+)))?/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw)) !== null) {
    const name = m[1].toLowerCase();
    const value = m[2] ?? m[3] ?? m[4] ?? "";
    out[name] = decodeEntities(value);
  }
  return out;
}

function decodeEntities(s: string): string {
  return s
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, "\"")
    .replace(/&#39;/g, "'");
}

// ---------------------------------------------------------------------------
// HTML → tree
// ---------------------------------------------------------------------------

export interface HtmlElement {
  tag: string;
  attrs: Record<string, string>;
  children: Array<HtmlElement | string>;
}

export function parseHtml(html: string): Array<HtmlElement | string> {
  const tokens = tokenizeHtml(html);
  const root: HtmlElement = { tag: "#document", attrs: {}, children: [] };
  const stack: HtmlElement[] = [root];
  let i = 0;
  while (i < tokens.length) {
    const tok = tokens[i];
    const parent = stack[stack.length - 1];
    if (tok.kind === "open") {
      const el: HtmlElement = { tag: tok.tag, attrs: tok.attrs, children: [] };
      parent.children.push(el);
      stack.push(el);
      i++;
    } else if (tok.kind === "close") {
      for (let j = stack.length - 1; j > 0; j--) {
        if (stack[j].tag === tok.tag) {
          stack.length = j;
          break;
        }
      }
      i++;
    } else if (tok.kind === "void") {
      parent.children.push({ tag: tok.tag, attrs: tok.attrs, children: [] });
      i++;
    } else {
      parent.children.push(tok.text);
      i++;
    }
  }
  return root.children;
}

// ---------------------------------------------------------------------------
// Style declarations
// ---------------------------------------------------------------------------

/** Map a CSS property name to its parsed value. */
export type StyleDecl = Record<string, string>;

export function parseStyleAttr(raw: string): StyleDecl {
  const out: StyleDecl = {};
  let depth = 0;
  let buf = "";
  for (let i = 0; i < raw.length; i++) {
    const c = raw[i];
    if (c === "(") depth++;
    else if (c === ")") depth--;
    else if (c === ";" && depth === 0) {
      commitDecl(buf, out);
      buf = "";
      continue;
    }
    buf += c;
  }
  if (buf.trim()) commitDecl(buf, out);
  return out;
}

function commitDecl(buf: string, out: StyleDecl): void {
  const idx = buf.indexOf(":");
  if (idx < 0) return;
  const k = buf.slice(0, idx).trim().toLowerCase();
  const v = buf.slice(idx + 1).trim();
  if (!k) return;
  out[k] = v;
}

// ---------------------------------------------------------------------------
// CSS string selector + ruleset parsing
// ---------------------------------------------------------------------------

export interface CssRule {
  /** Selector like ".btn" or "button:hover". */
  selector: string;
  /** Declarations. */
  decls: StyleDecl;
}

/** Parse a `<style>` block's body into rules. */
export function parseCssBody(body: string): CssRule[] {
  const rules: CssRule[] = [];
  let depth = 0;
  let selectorBuf = "";
  let bodyBuf = "";
  let inBody = false;
  for (let i = 0; i < body.length; i++) {
    const c = body[i];
    if (c === "{") {
      if (depth === 0) {
        // Transition from selector to body. SelectorBuf already holds the
        // accumulated selector text — DO NOT clear it. Reset for body.
        depth = 1;
        inBody = true;
        bodyBuf = "";
        continue;
      }
      bodyBuf += c;
      depth++;
    } else if (c === "}") {
      if (depth === 1) {
        // Close the current rule. selectorBuf has the selector text,
        // bodyBuf has the declarations.
        rules.push({
          selector: selectorBuf.trim(),
          decls: parseStyleAttr(bodyBuf),
        });
        selectorBuf = "";
        bodyBuf = "";
        depth = 0;
        inBody = false;
        continue;
      }
      bodyBuf += c;
      depth--;
    } else if (inBody) {
      bodyBuf += c;
    } else {
      selectorBuf += c;
    }
  }
  // Trim selectors
  return rules.map((r) => ({ selector: r.selector.replace(/\s+/g, " "), decls: r.decls }));
}

/** Extract <style>...</style> contents from an HTML string. */
export function extractStyleBlocks(html: string): string[] {
  const out: string[] = [];
  const re = /<style[^>]*>([\s\S]*?)<\/style>/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) {
    out.push(m[1]);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Style value parsing
// ---------------------------------------------------------------------------

const NAMED_COLORS: Record<string, [number, number, number]> = {
  black: [0, 0, 0],
  white: [1, 1, 1],
  red: [1, 0, 0],
  green: [0, 0.5, 0],
  blue: [0, 0, 1],
  yellow: [1, 1, 0],
  orange: [1, 0.42, 0.21],
  purple: [0.5, 0, 0.5],
  pink: [1, 0.41, 0.71],
  gray: [0.5, 0.5, 0.5],
  grey: [0.5, 0.5, 0.5],
  silver: [0.75, 0.75, 0.75],
  lime: [0, 1, 0],
  cyan: [0, 1, 1],
  magenta: [1, 0, 1],
  navy: [0, 0, 0.5],
  teal: [0, 0.5, 0.5],
  maroon: [0.5, 0, 0],
  olive: [0.5, 0.5, 0],
  transparent: [0, 0, 0],
};

export function parseColor(s: string): RGBA {
  s = s.trim();
  if (s === "transparent") return { r: 0, g: 0, b: 0, a: 0 };
  // #rgb / #rrggbb
  if (s.startsWith("#")) {
    const hex = s.slice(1);
    if (hex.length === 3) {
      const r = parseInt(hex[0] + hex[0], 16) / 255;
      const g = parseInt(hex[1] + hex[1], 16) / 255;
      const b = parseInt(hex[2] + hex[2], 16) / 255;
      return { r, g, b, a: 1 };
    }
    if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16) / 255;
      const g = parseInt(hex.slice(2, 4), 16) / 255;
      const b = parseInt(hex.slice(4, 6), 16) / 255;
      return { r, g, b, a: 1 };
    }
    if (hex.length === 8) {
      const r = parseInt(hex.slice(0, 2), 16) / 255;
      const g = parseInt(hex.slice(2, 4), 16) / 255;
      const b = parseInt(hex.slice(4, 6), 16) / 255;
      const a = parseInt(hex.slice(6, 8), 16) / 255;
      return { r, g, b, a };
    }
  }
  // rgb(r, g, b) / rgba(r, g, b, a)
  const rgbMatch = s.match(
    /^rgba?\(\s*([^,)\s]+)\s*[, ]\s*([^,)\s]+)\s*[, ]\s*([^,)\s/]+)(?:\s*[,/]\s*([^)]+))?\s*\)$/i,
  );
  if (rgbMatch) {
    const r = parseColorComponent(rgbMatch[1]);
    const g = parseColorComponent(rgbMatch[2]);
    const b = parseColorComponent(rgbMatch[3]);
    const a = rgbMatch[4] != null ? parseAlpha(rgbMatch[4]) : 1;
    return { r, g, b, a };
  }
  // hsl(h, s%, l%) / hsla(...)
  const hslMatch = s.match(
    /^hsla?\(\s*([\d.]+)(?:deg)?\s*[, ]\s*([\d.]+)%\s*[, ]\s*([\d.]+)%(?:\s*[,/]\s*([^)]+))?\s*\)$/i,
  );
  if (hslMatch) {
    const h = Number(hslMatch[1]) / 360;
    const sat = Number(hslMatch[2]) / 100;
    const l = Number(hslMatch[3]) / 100;
    const [r, g, b] = hslToRgb(h, sat, l);
    const a = hslMatch[4] != null ? parseAlpha(hslMatch[4]) : 1;
    return { r, g, b, a };
  }
  // Named color
  const named = NAMED_COLORS[s.toLowerCase()];
  if (named) return { r: named[0], g: named[1], b: named[2], a: 1 };
  // Unknown — assume black.
  return { r: 0, g: 0, b: 0, a: 1 };
}

function parseColorComponent(s: string): number {
  s = s.trim();
  if (s.endsWith("%")) {
    return Number(s.slice(0, -1)) / 100;
  }
  return Number(s) / 255;
}

function parseAlpha(s: string): number {
  s = s.trim();
  if (s.endsWith("%")) return Number(s.slice(0, -1)) / 100;
  return Number(s);
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  if (s === 0) {
    const v = l;
    return [v, v, v];
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [hue2rgb(p, q, h + 1 / 3), hue2rgb(p, q, h), hue2rgb(p, q, h - 1 / 3)];
}

function hue2rgb(p: number, q: number, t: number): number {
  if (t < 0) t += 1;
  if (t > 1) t -= 1;
  if (t < 1 / 6) return p + (q - p) * 6 * t;
  if (t < 1 / 2) return q;
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
  return p;
}

/**
 * Parse a CSS length into a number of CSS pixels.
 *
 *  - "12"        → 12
 *  - "12px"      → 12
 *  - "1.5rem"    → 24 (root font 16)
 *  - "1.2em"     → 19.2 (assumes 16px base; not context-aware)
 *  - "50%"       → NaN (caller must resolve with a containing size)
 *  - "100vh"     → -1 (sentinel — caller resolves)
 *  - "100vw"     → -2 (sentinel)
 *  - "50vmin"    → -3 (sentinel)
 *  - "50vmax"    → -4 (sentinel)
 */
export function parseLength(s: string, baseFontPx = 16): number {
  s = s.trim();
  if (s === "0") return 0;
  if (s === "auto") return NaN;
  if (s.endsWith("px")) return Number(s.slice(0, -2));
  if (s.endsWith("rem") || s.endsWith("em")) {
    return Number(s.slice(0, -3)) * baseFontPx;
  }
  if (s.endsWith("%")) return NaN; // caller resolves
  if (s.endsWith("vh")) {
    const n = Number(s.slice(0, -2));
    return -1 - n / 100; // negative sentinel — caller resolves
  }
  if (s.endsWith("vw")) {
    const n = Number(s.slice(0, -2));
    return -2 - n / 100;
  }
  if (s.endsWith("vmin")) {
    const n = Number(s.slice(0, -4));
    return -3 - n / 100;
  }
  if (s.endsWith("vmax")) {
    const n = Number(s.slice(0, -4));
    return -4 - n / 100;
  }
  // bare number
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
}

/** Parse a single radius token — "12px" or "50%". Percentages become
 *  fractions (50% → 0.5) and are resolved against the node's border-box
 *  by `resolveCornerRadii` at node-creation time. */
export function parseSingleRadius(s: string): number {
  if (s.endsWith("%")) return Number(s.slice(0, -1)) / 100;
  return parseLength(s);
}

/**
 * Resolve a CornerRadii against a node's border-box dimensions. CSS spec:
 * each percentage resolves against the corresponding axis of the border box.
 * NaN sentinels (vh/vw/vmin/vmax) resolve against the viewport.
 */
export function resolveCornerRadii(
  radii: CornerRadii,
  nodeW: number,
  nodeH: number,
  vw: number,
  vh: number,
  baseFontPx = 16,
): CornerRadii {
  const out: number[] = [0, 0, 0, 0];
  for (let i = 0; i < 4; i++) {
    const v = radii[i];
    if (v === undefined || !Number.isFinite(v)) {
      out[i] = 0;
    } else if (v > 0 && v < 1) {
      // Sub-1 value = fraction of axis (from parseRadii's 50% → 0.5 convention).
      out[i] = v * (i < 2 ? nodeW : nodeW);
    } else {
      out[i] = resolveLength(v, vw, vh, baseFontPx);
    }
  }
  return out as CornerRadii;
}

/** Resolve a parsed-length sentinel against viewport (CSS px). */
export function resolveLength(
  v: number,
  vw: number,
  vh: number,
  baseFontPx = 16,
): number {
  if (!Number.isFinite(v)) return 0;
  if (v <= -1 && v > -2) {
    // vh
    const pct = -1 - v;
    return pct * vh;
  }
  if (v <= -2 && v > -3) {
    const pct = -2 - v;
    return pct * vw;
  }
  if (v <= -3 && v > -4) {
    const pct = -3 - v;
    return pct * Math.min(vw, vh);
  }
  if (v <= -4) {
    const pct = -4 - v;
    return pct * Math.max(vw, vh);
  }
  void baseFontPx;
  return v;
}

/** Parse a CSS angle string into radians. */
export function parseAngle(s: string): number {
  s = s.trim();
  if (s.endsWith("deg")) return (Number(s.slice(0, -3)) * Math.PI) / 180;
  if (s.endsWith("rad")) return Number(s.slice(0, -3));
  if (s.endsWith("turn")) return Number(s.slice(0, -4)) * Math.PI * 2;
  if (s.endsWith("grad")) return (Number(s.slice(0, -4)) * Math.PI) / 200;
  const n = Number(s);
  return Number.isFinite(n) ? (n * Math.PI) / 180 : 0;
}

/** Parse "12px 4px" or "12px" into BorderWidths [top, right, bottom, left]. */
export function parseBorderWidths(s: string): BorderWidths {
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return [0, 0, 0, 0];
  if (parts.length === 1) {
    const v = parseLength(parts[0]);
    return [v, v, v, v];
  }
  if (parts.length === 2) {
    const a = parseLength(parts[0]);
    const b = parseLength(parts[1]);
    return [a, b, a, b];
  }
  if (parts.length === 3) {
    const a = parseLength(parts[0]);
    const b = parseLength(parts[1]);
    const c = parseLength(parts[2]);
    return [a, b, c, b];
  }
  return [
    parseLength(parts[0]),
    parseLength(parts[1]),
    parseLength(parts[2]),
    parseLength(parts[3]),
  ];
}

/** Parse "12px" or "12px 24px" or "12px 24px 36px 48px" into CornerRadii.
 *  Percentage values are stored as fractions (50% → 0.5) and resolved against
 *  the node's border-box by `resolveCornerRadii` at node-creation time. */
export function parseRadii(s: string): CornerRadii {
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return [0, 0, 0, 0];
  const parseOne = (tok: string): number => {
    if (tok.endsWith("%")) return Number(tok.slice(0, -1)) / 100;
    return parseLength(tok);
  };
  if (parts.length === 1) {
    const v = parseOne(parts[0]);
    return [v, v, v, v];
  }
  if (parts.length === 2) {
    const a = parseOne(parts[0]);
    const b = parseOne(parts[1]);
    return [a, b, a, b];
  }
  if (parts.length === 3) {
    const a = parseOne(parts[0]);
    const b = parseOne(parts[1]);
    const c = parseOne(parts[2]);
    return [a, b, c, b];
  }
  return [
    parseOne(parts[0]),
    parseOne(parts[1]),
    parseOne(parts[2]),
    parseOne(parts[3]),
  ];
}

/** Parse "1px 2px 3px 4px red" into a box-shadow descriptor. */
export interface ShadowParts {
  offsetX: number;
  offsetY: number;
  blur: number;
  spread: number;
  color: RGBA;
}

export function parseShadow(s: string): ShadowParts | null {
  // Find a color token — last word if it's not a length.
  const tokens = s.trim().split(/\s+/);
  let color = "rgba(0,0,0,0.5)";
  let offsetX = 0;
  let offsetY = 0;
  let blur = 0;
  let spread = 0;
  const lengths: number[] = [];
  for (const t of tokens) {
    if (/^-?[\d.]/.test(t)) {
      lengths.push(parseLength(t));
    } else {
      // assume color (last wins)
      color = tokens.slice(tokens.indexOf(t)).join(" ");
      break;
    }
  }
  if (lengths.length >= 1) offsetX = lengths[0];
  if (lengths.length >= 2) offsetY = lengths[1];
  if (lengths.length >= 3) blur = lengths[2];
  if (lengths.length >= 4) spread = lengths[3];
  return { offsetX, offsetY, blur, spread, color: parseColor(color) };
}

/** Parse a comma-separated list of shadows. */
export function parseShadowList(s: string): ShadowParts[] {
  // Shadows are comma-separated, but commas can appear inside rgba().
  // Strategy: scan, split on top-level commas (parens depth 0).
  const out: string[] = [];
  let depth = 0;
  let buf = "";
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (c === "(") depth++;
    else if (c === ")") depth--;
    else if (c === "," && depth === 0) {
      out.push(buf);
      buf = "";
      continue;
    }
    buf += c;
  }
  if (buf.trim()) out.push(buf);
  return out.map(parseShadow).filter((x): x is ShadowParts => x !== null);
}

/** Parse "transform: rotate(45deg) scale(1.2)" into rotation/scale. */
export interface TransformParts {
  rotation: number;
  scaleX: number;
  scaleY: number;
  translateX: number;
  translateY: number;
  hasTranslate: boolean;
  /** Set true if any part of the transform was unsupported (skew, matrix). */
  partial: boolean;
}

export function parseTransform(s: string): TransformParts {
  const out: TransformParts = {
    rotation: 0,
    scaleX: 1,
    scaleY: 1,
    translateX: 0,
    translateY: 0,
    hasTranslate: false,
    partial: false,
  };
  const re = /(rotate|translate|translateX|translateY|scale|scaleX|scaleY|skew|skewX|skewY|matrix)\s*\(\s*([^)]+)\s*\)/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s)) !== null) {
    const fn = m[1].toLowerCase();
    const args = m[2].split(/\s*,\s*|\s+/);
    switch (fn) {
      case "rotate":
        out.rotation = parseAngle(args[0]);
        break;
      case "translate":
        out.translateX = parseLength(args[0] ?? "0");
        out.translateY = parseLength(args[1] ?? "0");
        out.hasTranslate = true;
        break;
      case "translatex":
        out.translateX = parseLength(args[0] ?? "0");
        out.hasTranslate = true;
        break;
      case "translatey":
        out.translateY = parseLength(args[0] ?? "0");
        out.hasTranslate = true;
        break;
      case "scale":
        out.scaleX = Number(args[0] ?? 1);
        out.scaleY = args.length > 1 ? Number(args[1]) : out.scaleX;
        break;
      case "scalex":
        out.scaleX = Number(args[0] ?? 1);
        break;
      case "scaley":
        out.scaleY = Number(args[0] ?? 1);
        break;
      case "skew":
      case "skewx":
      case "skewy":
      case "matrix":
        out.partial = true;
        break;
    }
  }
  return out;
}

/** Parse "background: linear-gradient(...)" or a solid color. */
export interface BackgroundParts {
  type: "solid" | "linear-gradient" | "radial-gradient" | "image";
  color: RGBA;
  gradientEnd: RGBA;
  /** Angle in radians. π/2 = top-to-bottom. */
  angle: number;
  /** When type === "image": the URL extracted from url(...). */
  imageUrl?: string;
}

export function parseBackground(s: string): BackgroundParts | null {
  s = s.trim();
  const linearMatch = s.match(/^linear-gradient\(\s*(.+)\s*\)$/i);
  if (linearMatch) {
    return parseLinearGradient(linearMatch[1]);
  }
  const radialMatch = s.match(/^radial-gradient\(\s*(.+)\s*\)$/i);
  if (radialMatch) {
    return parseRadialGradient(radialMatch[1]);
  }
  // background-image: url(...) — pull the URL out. Quoted or unquoted.
  const urlMatch = s.match(/^url\(\s*(['"]?)([^'")]+)\1\s*\)$/i);
  if (urlMatch) {
    return {
      type: "image",
      color: { r: 0, g: 0, b: 0, a: 0 },
      gradientEnd: { r: 0, g: 0, b: 0, a: 0 },
      angle: 0,
      imageUrl: urlMatch[2],
    };
  }
  // Solid color (with possible image url we ignore)
  const stripped = s.replace(/url\([^)]+\)/g, "").trim();
  if (!stripped) return null;
  const c = parseColor(stripped);
  if (c.a === 0) return null;
  return { type: "solid", color: c, gradientEnd: c, angle: Math.PI / 2 };
}

function parseLinearGradient(args: string): BackgroundParts {
  // Optional angle or "to X" direction, then comma-separated color stops.
  let angle = Math.PI / 2; // top-to-bottom default
  let stopStr = args;
  const angleMatch = args.match(/^([\d.]+(?:deg|rad|turn|grad)?|to\s+(?:top|bottom|left|right)(?:\s+(?:top|bottom|left|right))?)\s*,\s*(.*)$/i);
  if (angleMatch) {
    const a = angleMatch[1].trim();
    if (a.startsWith("to ")) {
      const dir = a.slice(3).trim().toLowerCase();
      angle = directionToAngle(dir);
    } else {
      angle = parseAngle(a);
    }
    stopStr = angleMatch[2];
  }
  const stops = splitStops(stopStr);
  const colors = stops.map((s) => {
    const m = s.trim().match(/^(.+?)(?:\s+([\d.]+%|[\d.]+))?$/);
    if (!m) return parseColor(s.trim());
    return parseColor(m[1]);
  });
  const start = colors[0] ?? { r: 1, g: 1, b: 1, a: 1 };
  const end = colors[colors.length - 1] ?? start;
  return { type: "linear-gradient", color: start, gradientEnd: end, angle };
}

function parseRadialGradient(args: string): BackgroundParts {
  // PoC: render as 2-color "from-center" gradient. First and last stop colors.
  const stops = splitStops(args);
  const colors = stops.map((s) => parseColor(s.trim().split(/\s+/)[0] ?? "white"));
  const start = colors[0] ?? { r: 1, g: 1, b: 1, a: 1 };
  const end = colors[colors.length - 1] ?? start;
  return { type: "radial-gradient", color: start, gradientEnd: end, angle: 0 };
}

function splitStops(s: string): string[] {
  // Split on top-level commas (parens depth 0).
  const out: string[] = [];
  let depth = 0;
  let buf = "";
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (c === "(") depth++;
    else if (c === ")") depth--;
    else if (c === "," && depth === 0) {
      if (buf.trim()) out.push(buf.trim());
      buf = "";
      continue;
    }
    buf += c;
  }
  if (buf.trim()) out.push(buf.trim());
  return out;
}

function directionToAngle(dir: string): number {
  // CSS "to X" → angle in radians where 0 = right, π/2 = up (DOM), etc.
  if (dir === "top") return -Math.PI / 2;
  if (dir === "bottom") return Math.PI / 2;
  if (dir === "left") return Math.PI;
  if (dir === "right") return 0;
  if (dir === "top right") return -Math.PI / 4;
  if (dir === "top left") return -3 * Math.PI / 4;
  if (dir === "bottom right") return Math.PI / 4;
  if (dir === "bottom left") return 3 * Math.PI / 4;
  return Math.PI / 2;
}

/** Parse "filter: blur(8px) brightness(1.2)" into a FilterChain. */
export function parseFilter(s: string): FilterChain {
  const out: FilterChain = {
    blur: 0,
    brightness: 1,
    contrast: 1,
    saturate: 1,
    hueRotate: 0,
    invert: 0,
    backdropBlur: 0,
  };
  const re = /(blur|brightness|contrast|saturate|hue-rotate|invert|grayscale|sepia)\s*\(\s*([^)]+)\s*\)/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s)) !== null) {
    const fn = m[1].toLowerCase();
    const arg = m[2].trim();
    switch (fn) {
      case "blur":
        out.blur = parseLength(arg);
        break;
      case "brightness":
        out.brightness = parseFactor(arg);
        break;
      case "contrast":
        out.contrast = parseFactor(arg);
        break;
      case "saturate":
        out.saturate = parseFactor(arg);
        break;
      case "hue-rotate":
        out.hueRotate = parseAngle(arg);
        break;
      case "invert":
        out.invert = parseFactor(arg);
        break;
      case "grayscale":
      case "sepia":
        // Approximate as desaturate; PoC doesn't differentiate.
        out.saturate = 1 - parseFactor(arg);
        break;
    }
  }
  return out;
}

/**
 * Pull only the `blur(...)` radius out of a filter / backdrop-filter string.
 * Used so `backdrop-filter: blur(12px)` doesn't clobber a node's regular
 * `filter` chain — they coexist (different physical effects in CSS, different
 * fields in our FilterChain).
 */
export function parseBackdropBlur(s: string): number {
  const re = /blur\s*\(\s*([^)]+)\s*\)/i;
  const m = re.exec(s);
  if (!m) return 0;
  return parseLength(m[1].trim());
}

function parseFactor(s: string): number {
  if (s.endsWith("%")) return Number(s.slice(0, -1)) / 100;
  return Number(s);
}

// ---------------------------------------------------------------------------
// Selector matching
// ---------------------------------------------------------------------------

interface CompiledSelector {
  tag?: string;
  classes: string[];
  /** ":hover" | ":focus" | ":active" | ":disabled" — at most one for now. */
  pseudo?: string;
}

const SELECTOR_CACHE = new Map<string, CompiledSelector>();

function compileSelector(s: string): CompiledSelector | null {
  if (SELECTOR_CACHE.has(s)) return SELECTOR_CACHE.get(s)!;
  let out: CompiledSelector | null = null;
  let rest = s.trim();
  let pseudo: string | undefined;
  if (rest.includes(":")) {
    const colonIdx = rest.indexOf(":");
    pseudo = rest.slice(colonIdx + 1).toLowerCase();
    rest = rest.slice(0, colonIdx);
  }
  // Strip descendant combinator: split on space.
  const parts = rest.split(/\s+/).filter(Boolean);
  const last = parts[parts.length - 1] ?? "";
  // Detect "no tag" (e.g. ".foo" — starts with ".").
  const startsWithClass = last.startsWith(".") || last.startsWith("#");
  const classList = last.split(/[.#]/).filter(Boolean);
  const tag = startsWithClass || classList.length === 0
    ? undefined
    : classList[0];
  const classes = startsWithClass ? classList : classList.slice(1);
  out = { tag, classes, pseudo };
  SELECTOR_CACHE.set(s, out);
  return out;
}

function elementMatches(
  sel: CompiledSelector,
  el: HtmlElement,
  isHovered: boolean,
  isFocused: boolean,
  isActive: boolean,
): boolean {
  if (sel.tag && sel.tag !== el.tag) return false;
  for (const c of sel.classes) {
    if (!(` ${el.attrs.class ?? ""} `.split(/\s+/).includes(c))) return false;
  }
  if (sel.pseudo === "hover" && !isHovered) return false;
  if (sel.pseudo === "focus" && !isFocused) return false;
  if (sel.pseudo === "active" && !isActive) return false;
  return true;
}

// ---------------------------------------------------------------------------
// Computed style resolution
// ---------------------------------------------------------------------------

const BASE_STYLE: ComputedStyle = {
  fill: { r: 1, g: 1, b: 1, a: 0 },
  radii: [0, 0, 0, 0],
  borderWidths: [0, 0, 0, 0],
  borderColor: { r: 0, g: 0, b: 0, a: 1 },
  strokeWidth: 0,
  stroke: { r: 0, g: 0, b: 0, a: 1 },
  shadowOffsetX: 0,
  shadowOffsetY: 0,
  shadowBlur: 0,
  shadowSpread: 0,
  shadowColor: { r: 0, g: 0, b: 0, a: 0 },
  opacity: 1,
  rotation: 0,
  scaleX: 1,
  scaleY: 1,
  transformOriginX: 0.5,
  transformOriginY: 0.5,
  mixBlendMode: "normal",
  visibility: "visible",
  pointerEvents: "auto",
  overflow: "visible",
  filter: { blur: 0, brightness: 1, contrast: 1, saturate: 1, hueRotate: 0, invert: 0, backdropBlur: 0 },
  fontSize: 16,
  fontWeight: 400,
  fontFamily: "system-ui, -apple-system, sans-serif",
  textColor: { r: 0.1, g: 0.1, b: 0.1, a: 1 },
  textWrap: false,
  lineHeight: 1.2,
  letterSpacing: 0,
  textAlign: "left",
  textOverflowEllipsis: false,
  display: "block",
  flexDirection: "row",
  justifyContent: "flex-start",
  alignItems: "stretch",
  flexWrap: "nowrap",
  alignSelf: "auto",
  flexGrow: 0,
  flexShrink: 1,
  flexBasis: "auto",
  gap: 0,
  // CSS Grid — track templates. Empty = no explicit grid (auto-fill).
  gridTemplateColumns: "",
  gridTemplateRows: "",
};

interface ApplyContext {
  viewportW: number;
  viewportH: number;
  baseFontPx: number;
  /** True when resolving this element's style under :hover state. */
  isHovered: boolean;
  isFocused: boolean;
  isActive: boolean;
}

function applyDecls(
  base: ComputedStyle,
  decls: StyleDecl,
  ctx: ApplyContext,
): ComputedStyle {
  const out: ComputedStyle = JSON.parse(JSON.stringify(base));
  const setL = (name: keyof ComputedStyle, v: unknown) => {
    (out as unknown as Record<string, unknown>)[name] = v;
  };
  for (const [k, v] of Object.entries(decls)) {
    const value = v.trim();
    switch (k) {
      case "background":
      case "background-color": {
        const bg = parseBackground(value);
        if (bg) {
          out.fill = bg.color;
          if (bg.type === "linear-gradient" || bg.type === "radial-gradient") {
            out.gradientEnd = bg.gradientEnd;
            out.gradientAngle = bg.angle;
          } else if (bg.type === "image") {
            out.imageUrl = bg.imageUrl;
          }
        }
        break;
      }
      case "background-image": {
        const bg = parseBackground(value);
        if (bg) {
          // Image url wins over fill color. Gradient attrs are left alone
          // (FS won't apply them when uUseFillTex=1).
          out.imageUrl = bg.imageUrl ?? out.imageUrl;
          if (bg.type === "linear-gradient" || bg.type === "radial-gradient") {
            out.fill = bg.color;
            out.gradientEnd = bg.gradientEnd;
            out.gradientAngle = bg.angle;
          }
        }
        break;
      }
      case "color":
        out.textColor = parseColor(value);
        out.fill = out.textColor;
        break;
      case "opacity":
        out.opacity = Number(value);
        break;
      case "visibility":
        out.visibility = value === "hidden" ? "hidden" : "visible";
        break;
      case "pointer-events":
        out.pointerEvents = value === "none" ? "none" : "auto";
        break;
      case "overflow":
        out.overflow =
          value === "hidden" ? "hidden" :
          value === "scroll" || value === "auto" ? "scroll" :
          "visible";
        break;
      case "border-radius":
        out.radii = parseRadii(value);
        break;
      case "border-top-left-radius":
        out.radii = [
          parseSingleRadius(value),
          out.radii?.[1] ?? 0,
          out.radii?.[2] ?? 0,
          out.radii?.[3] ?? 0,
        ];
        break;
      case "border-top-right-radius":
        out.radii = [
          out.radii?.[0] ?? 0,
          parseSingleRadius(value),
          out.radii?.[2] ?? 0,
          out.radii?.[3] ?? 0,
        ];
        break;
      case "border-bottom-right-radius":
        out.radii = [
          out.radii?.[0] ?? 0,
          out.radii?.[1] ?? 0,
          parseSingleRadius(value),
          out.radii?.[3] ?? 0,
        ];
        break;
      case "border-bottom-left-radius":
        out.radii = [
          out.radii?.[0] ?? 0,
          out.radii?.[1] ?? 0,
          out.radii?.[2] ?? 0,
          parseSingleRadius(value),
        ];
        break;
      case "border":
      case "border-top":
      case "border-right":
      case "border-bottom":
      case "border-left": {
        const sides = parseBorderShorthand(value);
        const idx =
          k === "border-top" ? 0 :
          k === "border-right" ? 1 :
          k === "border-bottom" ? 2 :
          k === "border-left" ? 3 : -1;
        if (idx < 0) {
          out.borderWidths = sides.widths;
        } else {
          out.borderWidths = [...out.borderWidths] as BorderWidths;
          (out.borderWidths as number[])[idx] = sides.widths[idx === -1 ? 0 : idx];
        }
        if (sides.color) out.borderColor = sides.color;
        break;
      }
      case "border-color":
        out.borderColor = parseColor(value);
        break;
      case "border-width":
        out.borderWidths = parseBorderWidths(value);
        break;
      case "box-shadow": {
        const shadows = parseShadowList(value);
        // PoC: use the first shadow only.
        const s = shadows[0];
        if (s) {
          out.shadowOffsetX = s.offsetX;
          out.shadowOffsetY = s.offsetY;
          out.shadowBlur = s.blur;
          out.shadowSpread = s.spread;
          out.shadowColor = s.color;
        }
        break;
      }
      case "font-size":
        out.fontSize = Math.max(1, parseLength(value, ctx.baseFontPx));
        break;
      case "font-weight":
        out.fontWeight = Number(value) || 400;
        break;
      case "font-family":
        out.fontFamily = value;
        break;
      case "font-style":
        // PoC: ignore (italic). Could map to font-weight 401 etc.
        break;
      case "line-height":
        out.lineHeight = value.endsWith("px")
          ? Number(value.slice(0, -2)) / out.fontSize
          : Number(value) || 1.2;
        break;
      case "letter-spacing":
        out.letterSpacing = parseLength(value);
        break;
      case "text-align":
        out.textAlign = (["left", "center", "right"] as const).find((x) => x === value) ?? "left";
        break;
      case "text-transform":
        // PoC: not applied (would need text post-processing).
        break;
      case "text-decoration":
        // PoC: not applied (could draw an underline rect).
        break;
      case "text-overflow":
        out.textOverflowEllipsis = value === "ellipsis";
        break;
      case "white-space":
        out.textWrap = value === "normal" || value === "pre-line";
        break;
      case "transform": {
        const t = parseTransform(value);
        out.rotation = t.rotation;
        out.scaleX = t.scaleX;
        out.scaleY = t.scaleY;
        if (t.hasTranslate) {
          out.filter = { ...out.filter };
        }
        break;
      }
      case "transform-origin": {
        const parts = value.split(/\s+/);
        out.transformOriginX = parseOrigin(parts[0] ?? "50%", out.filter, ctx);
        out.transformOriginY = parseOrigin(parts[1] ?? "50%", out.filter, ctx);
        break;
      }
      case "filter":
        out.filter = parseFilter(value);
        break;
      case "backdrop-filter":
        // Only the blur() component matters for our glass-card effect; this
        // preserves any `filter:` values already on the node.
        out.filter ??= {
          blur: 0,
          brightness: 1,
          contrast: 1,
          saturate: 1,
          hueRotate: 0,
          invert: 0,
          backdropBlur: 0,
        };
        out.filter.backdropBlur = parseBackdropBlur(value);
        break;
      case "mix-blend-mode":
        out.mixBlendMode = value;
        break;
      case "display":
        out.display = value;
        break;
      case "flex-direction":
        out.flexDirection = value;
        break;
      case "flex-wrap":
        out.flexWrap = value;
        break;
      case "justify-content":
        out.justifyContent = value;
        break;
      case "align-items":
        out.alignItems = value;
        break;
      case "align-self":
        out.alignSelf = value;
        break;
      case "flex-grow":
        out.flexGrow = Number(value);
        break;
      case "flex-shrink":
        out.flexShrink = Number(value);
        break;
      case "flex-basis":
        out.flexBasis = value;
        break;
      case "flex": {
        // Shorthand `flex: <grow> [<shrink> [<basis>]]`. Most common form is
        // `flex: 1` (grow only, basis defaults to 0). Parse tokens; only
        // expand grow + shrink since flexBasis needs a length-aware parser
        // we don't have here. Anything else is silently ignored.
        const parts = value.trim().split(/\s+/);
        const g = Number(parts[0]);
        if (Number.isFinite(g)) out.flexGrow = g;
        if (parts.length >= 2) {
          const s = Number(parts[1]);
          if (Number.isFinite(s)) out.flexShrink = s;
        }
        break;
      }
      case "gap":
      case "grid-gap":
        out.gap = parseLength(value);
        break;
      case "row-gap":
      case "column-gap":
        // For PoC we apply row-gap only (column uses same).
        out.gap = parseLength(value);
        break;
      case "grid-template-columns":
        out.gridTemplateColumns = value.trim();
        break;
      case "grid-template-rows":
        out.gridTemplateRows = value.trim();
        break;
      case "width":
      case "height":
      case "min-width":
      case "max-width":
      case "min-height":
      case "max-height":
      case "top":
      case "right":
      case "bottom":
      case "left":
      case "margin":
      case "padding":
        // Layout properties are handled in the layout pass, not the style.
        break;
      default:
        // Silently ignore unknown properties.
        break;
    }
    void setL;
  }
  return out;
}

function parseBorderShorthand(value: string): { widths: BorderWidths; color: RGBA | null } {
  // "2px solid red" → widths [2,2,2,2] + color red.
  const parts = value.split(/\s+/);
  const widths: number[] = [];
  let color: string | null = null;
  for (const p of parts) {
    if (/^[\d.]/.test(p)) widths.push(parseLength(p));
    else if (p === "solid" || p === "dashed" || p === "dotted" || p === "none") continue;
    else color = color ? `${color} ${p}` : p;
  }
  const w = widths.length > 0 ? widths : [0];
  return {
    widths: [w[0] ?? 0, w[0] ?? 0, w[0] ?? 0, w[0] ?? 0],
    color: color ? parseColor(color) : null,
  };
}

function parseOrigin(s: string, _filter: FilterChain, _ctx: ApplyContext): number {
  s = s.trim();
  if (s === "center") return 0.5;
  if (s === "left" || s === "top") return 0;
  if (s === "right" || s === "bottom") return 1;
  if (s.endsWith("%")) return Number(s.slice(0, -1)) / 100;
  if (s.endsWith("px")) {
    // Translate px origin to a 0..1 scale relative to the node's size; this
    // is a placeholder — proper handling requires the layout pass to set
    // this AFTER the node's size is known.
    return Number(s.slice(0, -2)) / 100;
  }
  const n = Number(s);
  return Number.isFinite(n) ? n / 100 : 0.5;
}

// ---------------------------------------------------------------------------
// Layout — block + flex (the "real CSS" subset)
// ---------------------------------------------------------------------------

interface LayoutBox {
  el: HtmlElement;
  style: ComputedStyle;
  /** Resolved CSS px box. */
  x: number;
  y: number;
  w: number;
  h: number;
  /** True if the element had explicit width/max-width in inline style. */
  hasExplicitW?: boolean;
  /** True if the element had explicit height/max-height in inline style. */
  hasExplicitH?: boolean;
  /** Margin [top, right, bottom, left] in CSS px. */
  margin: BorderWidths;
  /** Padding [top, right, bottom, left] in CSS px. */
  padding: BorderWidths;
  children: LayoutBox[];
  /** Inline CSS text on this element. */
  styleAttr: string;
  /** CSS rules from the <style> block that target this element. */
  matchingDecls: StyleDecl[];
}

function isBlock(style: ComputedStyle): boolean {
  return style.display === "block" || style.display === "flex" || style.display === "grid";
}

function collectInlineDecls(el: HtmlElement): StyleDecl {
  if (!el.attrs.style) return {};
  return parseStyleAttr(el.attrs.style);
}

function collectMatchingDecls(
  el: HtmlElement,
  rules: CssRule[],
  ctx: ApplyContext,
): StyleDecl[] {
  const out: StyleDecl[] = [];
  for (const r of rules) {
    const sel = compileSelector(r.selector);
    if (!sel) continue;
    if (
      elementMatches(
        sel,
        el,
        ctx.isHovered,
        ctx.isFocused,
        ctx.isActive,
      )
    ) {
      out.push(r.decls);
    }
  }
  return out;
}

function resolveStyle(
  el: HtmlElement,
  rules: CssRule[],
  ctx: ApplyContext,
  parentStyle: ComputedStyle | null,
): ComputedStyle {
  const decls: StyleDecl[] = [];
  decls.push(...collectMatchingDecls(el, rules, ctx));
  decls.push(collectInlineDecls(el));
  let s = BASE_STYLE;
  for (const d of decls) {
    s = applyDecls(s, d, ctx);
  }
  // Inherit font-size from parent if not set.
  if (parentStyle && s.fontSize === BASE_STYLE.fontSize) {
    s.fontSize = parentStyle.fontSize;
  }
  return s;
}

function parseBoxShorthand(value: string, _ctx: ApplyContext): BorderWidths {
  return parseBorderWidths(value);
}

/**
 * Run the layout pass. Produces a tree of LayoutBox nodes with computed x/y/w/h.
 *
 * Algorithm:
 *   1. For each direct child element, compute its box dimensions (width/height
 *      respecting %, max-*, etc.) and place it in a flex/block layout if its
 *      parent is a container.
 *   2. The flex/block container's layoutFlex() assigns each child a position
 *      (relative to the container's content origin) and a size.
 *   3. After child positions are known, each child's own children are laid
 *      out by walking box.children — they inherit the child's position as
 *      their container origin. Positions from the parent are NOT overwritten.
 */
function runLayout(
  elements: Array<HtmlElement | string>,
  parentBox: { x: number; y: number; w: number; h: number } | null,
  rules: CssRule[],
  ctx: ApplyContext,
  parentStyle: ComputedStyle | null,
): LayoutBox[] {
  const out: LayoutBox[] = [];
  const containerX = parentBox?.x ?? 0;
  const containerY = parentBox?.y ?? 0;
  const containerW = parentBox?.w ?? ctx.viewportW;
  const containerH = parentBox?.h ?? ctx.viewportH;

  for (const el of elements) {
    if (typeof el === "string") continue;
    const style = resolveStyle(el, rules, ctx, parentStyle);
    if (style.display === "none") continue;
    const inlineStyle = collectInlineDecls(el);
    const margin = inlineStyle.margin
      ? parseBoxShorthand(inlineStyle.margin, ctx)
      : ([0, 0, 0, 0] as BorderWidths);
    const padding = inlineStyle.padding
      ? parseBoxShorthand(inlineStyle.padding, ctx)
      : ([0, 0, 0, 0] as BorderWidths);

    // Resolve width/height from inline style. Supports px, %, and 'auto'/'100%'.
    const hasW = !!(inlineStyle.width || inlineStyle["max-width"]);
    const hasH = !!(inlineStyle.height || inlineStyle["max-height"]);
    const w = hasW
      ? resolveBoxSize(inlineStyle.width, inlineStyle["max-width"], containerW, ctx, style.fontSize)
      : containerW;
    const h = hasH
      ? resolveBoxSize(inlineStyle.height, inlineStyle["max-height"], containerH, ctx, style.fontSize)
      : containerH;
    const inlinePos = inlineStyle.position ?? "static";
    const inlineTop = inlineStyle.top ? parseLength(inlineStyle.top, style.fontSize) : NaN;
    const inlineLeft = inlineStyle.left ? parseLength(inlineStyle.left, style.fontSize) : NaN;

    // Initial position — will be overwritten by parent's layoutFlex if the parent
    // is a flex/block container, or by absolute positioning below.
    let x = containerX + margin[3];
    let y = containerY + margin[0];

    if (inlinePos === "absolute" && parentBox) {
      x = containerX + (Number.isFinite(inlineLeft) ? resolveLength(inlineLeft, ctx.viewportW, ctx.viewportH, style.fontSize) : 0);
      y = containerY + (Number.isFinite(inlineTop) ? resolveLength(inlineTop, ctx.viewportW, ctx.viewportH, style.fontSize) : 0);
    }

    const box: LayoutBox = {
      el,
      style,
      x, y, w, h,
      hasExplicitW: hasW,
      hasExplicitH: hasH,
      margin, padding,
      children: [],
      styleAttr: el.attrs.style ?? "",
      matchingDecls: collectMatchingDecls(el, rules, ctx),
    };

    // Lay out children if this is a flex/block/grid container.
    if (style.display === "flex") {
      box.children = layoutFlexChildren(el, box, rules, ctx, style);
    } else if (style.display === "grid") {
      box.children = layoutGridChildren(el, box, rules, ctx, style);
    } else if (isBlock(style)) {
      box.children = layoutBlockChildren(el, box, rules, ctx, style);
    } else {
      // Inline leaf — auto-size to content height.
      const text = (el.children.find((c) => typeof c === "string") as string | undefined) ?? "";
      box.h = style.fontSize * style.lineHeight;
      // Don't override width — it may have been set explicitly.
      if (!Number.isFinite(w) || w === 0) box.w = containerW;
      box.children = [];
      void text;
    }

    out.push(box);

    // Recurse into children with their own computed positions as the container.
    for (const childBox of box.children) {
      layoutChildBox(childBox, rules, ctx, style, out);
    }
  }

  return out;
}

/**
 * Process a single child box's layout: lay out its children using flex/block
 * as appropriate, then recursively descend. The child's own x/y/w/h were
 * already set by its parent's layoutFlex/layoutBlock.
 */
function layoutChildBox(
  box: LayoutBox,
  rules: CssRule[],
  ctx: ApplyContext,
  parentStyle: ComputedStyle,
  out: LayoutBox[],
): void {
  const inlineStyle = box.styleAttr ? parseStyleAttr(box.styleAttr) : {};
  const el = box.el;
  const style = box.style;

  // Determine if this box needs children laid out via flex/block/grid.
  if (style.display === "flex") {
    box.children = layoutFlexChildren(el, box, rules, ctx, style);
  } else if (style.display === "grid") {
    box.children = layoutGridChildren(el, box, rules, ctx, style);
  } else if (isBlock(style)) {
    box.children = layoutBlockChildren(el, box, rules, ctx, style);
  } else {
    // Inline leaf — children are already handled (none in practice for leaves).
    if (!box.children || box.children.length === 0) {
      const text = (el.children.find((c) => typeof c === "string") as string | undefined) ?? "";
      box.h = style.fontSize * style.lineHeight;
      if (box.w === 0) box.w = ctx.viewportW;
      box.children = [];
      void text;
    }
  }

  if (!out.includes(box)) out.push(box);

  for (const childBox of box.children) {
    layoutChildBox(childBox, rules, ctx, box.style, out);
  }
}

/**
 * Resolve a CSS box dimension (width/height) from inline style, supporting
 * px, %, auto (NaN), and max-* caps. Resolves against parentContainerSize.
 */
function resolveBoxSize(
  raw: string | undefined,
  rawMax: string | undefined,
  parentContainerSize: number,
  ctx: ApplyContext,
  fontSize: number,
): number {
  const parsed = raw ? parseLength(raw, fontSize) : NaN;
  let size: number;
  if (!Number.isFinite(parsed)) {
    // auto or unset — caller will treat as fill-parent for containers, auto for leaves.
    size = parentContainerSize;
  } else if (parsed < 0) {
    // % was returned as NaN by parseLength, but vh/vw use negative sentinels.
    // Those should be resolved against viewport here.
    size = resolveLength(parsed, ctx.viewportW, ctx.viewportH, fontSize);
  } else {
    // For px values from inline: a literal % value isn't returned as a positive
    // number by parseLength — it returns NaN. So this branch is px.
    size = parsed;
  }
  // If the raw value contains '%', parseLength returned NaN. We need to reparse.
  if (raw && raw.trim().endsWith("%")) {
    const pct = Number(raw.trim().slice(0, -1));
    if (Number.isFinite(pct)) {
      size = (pct / 100) * parentContainerSize;
    }
  }
  // Apply max-* cap.
  if (rawMax) {
    const maxParsed = parseLength(rawMax, fontSize);
    if (Number.isFinite(maxParsed)) {
      let maxSize: number;
      if (maxParsed < 0) {
        maxSize = resolveLength(maxParsed, ctx.viewportW, ctx.viewportH, fontSize);
      } else {
        maxSize = maxParsed;
      }
      if (rawMax.trim().endsWith("%")) {
        const pct = Number(rawMax.trim().slice(0, -1));
        if (Number.isFinite(pct)) maxSize = (pct / 100) * parentContainerSize;
      }
      size = Math.min(size, maxSize);
    }
  }
  return size;
}

/**
 * Layout flex children. Returns the children as LayoutBoxes with positions
 * and sizes assigned. Children's x/y are RELATIVE to the container's
 * content-area origin (so add padding[3] and padding[0] in the caller if
 * needed — but here we add them so the returned children are in container
 * coordinates that include padding offset).
 */
function layoutFlexChildren(
  el: HtmlElement,
  box: LayoutBox,
  rules: CssRule[],
  ctx: ApplyContext,
  style: ComputedStyle,
): LayoutBox[] {
  const innerW = box.w - box.padding[1] - box.padding[3];
  const innerH = box.h - box.padding[0] - box.padding[2];
  const kids = el.children.filter((c): c is HtmlElement => typeof c !== "string");
  const childBoxes: LayoutBox[] = [];

  for (const k of kids) {
    const childStyle = resolveStyle(k, rules, ctx, style);
    if (childStyle.display === "none") continue;
    const childInline = collectInlineDecls(k);
    const childMargin = childInline.margin
      ? parseBoxShorthand(childInline.margin, ctx)
      : ([0, 0, 0, 0] as BorderWidths);
    const childPad = childInline.padding
      ? parseBoxShorthand(childInline.padding, ctx)
      : ([0, 0, 0, 0] as BorderWidths);

    // Resolve child width/height against THIS container's inner area.
    // If no explicit height/max-height, leave at 0 so layoutFlex computes intrinsic.
    const hasW = !!(childInline.width || childInline["max-width"]);
    const hasH = !!(childInline.height || childInline["max-height"]);
    const cw = hasW
      ? resolveBoxSize(childInline.width, childInline["max-width"], innerW, ctx, childStyle.fontSize)
      : 0;
    const ch = hasH
      ? resolveBoxSize(childInline.height, childInline["max-height"], innerH, ctx, childStyle.fontSize)
      : 0;
    const cb: LayoutBox = {
      el: k,
      style: childStyle,
      x: 0, y: 0,
      w: cw,
      h: ch,
      hasExplicitW: hasW,
      hasExplicitH: hasH,
      margin: childMargin,
      padding: childPad,
      children: [],
      styleAttr: k.attrs.style ?? "",
      matchingDecls: collectMatchingDecls(k, rules, ctx),
    };
    // Apply cross-axis stretch when no explicit cross-axis size was given.
    // For column flex: main=Y, cross=X → stretch width.
    // For row flex: main=X, cross=Y → stretch height.
    if (!hasW) {
      if (style.flexDirection === "row" || style.flexDirection === "row-reverse") {
        // main axis is X — leave cw at 0; layoutFlex will compute intrinsic.
      } else {
        cb.w = innerW;
      }
    }
    if (!hasH) {
      if (style.flexDirection === "column") {
        // main axis is Y — leave ch at 0; layoutFlex will compute intrinsic.
      } else {
        cb.h = innerH;
      }
    }
    childBoxes.push(cb);
  }

  layoutFlex(style, childBoxes, innerW, innerH);

  // Add container origin + padding offset so children carry absolute positions
  // matching their final viewport coordinates. This is what buildNode reads
  // directly — child box.x/y are absolute, not relative.
  for (const c of childBoxes) {
    c.x += box.x + box.padding[3];
    c.y += box.y + box.padding[0];
  }

  // Update container height to fit content if container has no explicit height.
  if (!box.hasExplicitH) {
    if (style.flexDirection === "row" || style.flexDirection === "row-reverse") {
      const rowH = childBoxes.reduce((m, c) => Math.max(m, c.h + c.margin[0] + c.margin[2]), 0);
      box.h = rowH + box.padding[0] + box.padding[2];
    } else {
      const colH = childBoxes.reduce((s, c) => s + c.h + c.margin[0] + c.margin[2], 0)
        + Math.max(0, childBoxes.length - 1) * style.gap;
      box.h = colH + box.padding[0] + box.padding[2];
    }
  }

  return childBoxes;
}

/**
 * Layout block children — stack vertically with intrinsic widths.
 */
function layoutBlockChildren(
  el: HtmlElement,
  box: LayoutBox,
  rules: CssRule[],
  ctx: ApplyContext,
  style: ComputedStyle,
): LayoutBox[] {
  const innerW = box.w - box.padding[1] - box.padding[3];
  const kids = el.children.filter((c): c is HtmlElement => typeof c !== "string");
  const childBoxes: LayoutBox[] = [];
  let cursorY = box.padding[0];

  for (const k of kids) {
    const childStyle = resolveStyle(k, rules, ctx, style);
    if (childStyle.display === "none") continue;
    const childInline = collectInlineDecls(k);
    const childMargin = childInline.margin
      ? parseBoxShorthand(childInline.margin, ctx)
      : ([0, 0, 0, 0] as BorderWidths);
    const childPad = childInline.padding
      ? parseBoxShorthand(childInline.padding, ctx)
      : ([0, 0, 0, 0] as BorderWidths);
    const hasChildW = !!(childInline.width || childInline["max-width"]);
    const hasChildH = !!(childInline.height || childInline["max-height"]);
    const cw = resolveBoxSize(childInline.width, childInline["max-width"], innerW, ctx, childStyle.fontSize);
    const ch = resolveBoxSize(childInline.height, childInline["max-height"], 0, ctx, childStyle.fontSize);
    const cb: LayoutBox = {
      el: k,
      style: childStyle,
      x: box.x + box.padding[3],
      y: box.y + cursorY + childMargin[0],
      w: Number.isFinite(cw) && cw > 0 ? cw : innerW,
      h: Number.isFinite(ch) && ch > 0 ? ch : childStyle.fontSize * childStyle.lineHeight,
      hasExplicitW: hasChildW,
      hasExplicitH: hasChildH,
      margin: childMargin,
      padding: childPad,
      children: [],
      styleAttr: k.attrs.style ?? "",
      matchingDecls: collectMatchingDecls(k, rules, ctx),
    };
    childBoxes.push(cb);
    cursorY += cb.h + childMargin[0] + childMargin[2];
  }

  // Update container height if no explicit height was set.
  if (!box.hasExplicitH) {
    if (childBoxes.length === 0) {
      // No element children — keep the height set by parent's flex layout
      // (which used fontSize * lineHeight as intrinsic for text leaves).
      // If we don't have one, fall back to that default here.
      if (box.h === 0) box.h = style.fontSize * style.lineHeight;
    } else {
      box.h = cursorY + box.padding[2];
    }
  }

  return childBoxes;
}

function layoutFlex(
  containerStyle: ComputedStyle,
  children: LayoutBox[],
  innerW: number,
  innerH: number,
): void {
  if (children.length === 0) return;
  const isRow = containerStyle.flexDirection === "row" || containerStyle.flexDirection === "row-reverse";
  const gap = containerStyle.gap;
  const totalGap = gap * Math.max(0, children.length - 1);
  if (isRow) {
    // First pass: assign intrinsic widths.
    let fixedW = 0;
    let growTotal = 0;
    for (const c of children) {
      const intrinsicW = c.w > 0 ? c.w : c.style.fontSize * (c.el.attrs.value?.length ?? 4);
      c.w = intrinsicW;
      fixedW += intrinsicW;
      growTotal += c.style.flexGrow;
    }
    const remaining = Math.max(0, innerW - fixedW - totalGap);
    if (growTotal > 0) {
      for (const c of children) {
        const extra = remaining * (c.style.flexGrow / growTotal);
        c.w += extra;
      }
    }
    // Position.
    let cursor = 0;
    const justify = containerStyle.justifyContent;
    const totalW = children.reduce((s, c) => s + c.w, 0);
    const freeSpace = Math.max(0, innerW - totalW);
    let extraGap = 0;
    let startOffset = 0;
    if (justify === "center") startOffset = freeSpace * 0.5;
    else if (justify === "flex-end") startOffset = freeSpace;
    else if (justify === "space-between") {
      startOffset = 0;
      // Distribute remaining space BETWEEN children, not at the start.
      extraGap = children.length > 1 ? freeSpace / (children.length - 1) : 0;
    } else if (justify === "space-around") {
      const each = children.length > 0 ? freeSpace / (children.length * 2) : 0;
      startOffset = each;
      extraGap = each;
    } else if (justify === "space-evenly") {
      const each = children.length > 0 ? freeSpace / (children.length + 1) : 0;
      startOffset = each;
      extraGap = each;
    }
    cursor = startOffset;
    for (let i = 0; i < children.length; i++) {
      const c = children[i];
      c.x = cursor;
      cursor += c.w;
      if (i < children.length - 1) cursor += gap + extraGap;
      // align-items for cross-axis.
      const ca = c.style.alignSelf !== "auto" ? c.style.alignSelf : containerStyle.alignItems;
      if (ca === "center") c.y = (innerH - c.h) * 0.5;
      else if (ca === "flex-end") c.y = innerH - c.h;
      else c.y = 0;
    }
  } else {
    // column layout — stack vertically.
    let cursor = 0;
    let fixedH = 0;
    let growTotal = 0;
    for (const c of children) {
      const intrinsicH = c.h > 0 ? c.h : c.style.fontSize * c.style.lineHeight;
      c.h = intrinsicH;
      fixedH += intrinsicH;
      growTotal += c.style.flexGrow;
    }
    const remaining = Math.max(0, innerH - fixedH - totalGap);
    if (growTotal > 0) {
      for (const c of children) {
        const extra = remaining * (c.style.flexGrow / growTotal);
        c.h += extra;
      }
    }
    const justify = containerStyle.justifyContent;
    const totalH = children.reduce((s, c) => s + c.h, 0) + totalGap;
    let startOffset = 0;
    if (justify === "center") startOffset = (innerH - totalH) * 0.5;
    else if (justify === "flex-end") startOffset = innerH - totalH;
    cursor = startOffset;
    for (let i = 0; i < children.length; i++) {
      const c = children[i];
      c.y = cursor;
      cursor += c.h;
      if (i < children.length - 1) cursor += gap;
      const ca = c.style.alignSelf !== "auto" ? c.style.alignSelf : containerStyle.alignItems;
      if (ca === "center") c.x = (innerW - c.w) * 0.5;
      else if (ca === "flex-end") c.x = innerW - c.w;
      else c.x = 0;
    }
  }
}

// ---------------------------------------------------------------------------
// Layout → scene nodes
// ---------------------------------------------------------------------------

function buildSceneFromLayout(
  layout: LayoutBox[],
  scene: Scene,
  baseZ: number,
  viewportW: number,
  viewportH: number,
): number {
  // The `layout` array is already the full flat DOM walk produced by
  // runLayout (which descends into box.children for every container).
  // No recursion here — just emit each box once in flat order.
  let z = baseZ;
  for (const box of layout) {
    const node = buildNode(box, scene, z, viewportW, viewportH);
    if (node) z++;
  }
  return z;
}

function buildNode(box: LayoutBox, scene: Scene, z: number, viewportW: number, viewportH: number): SceneNode | null {
  const el = box.el;
  const isTextEl = (el.children.length === 1 && typeof el.children[0] === "string");
  const inline = box.styleAttr ? parseStyleAttr(box.styleAttr) : {};
  const shadow = inline["box-shadow"] ? parseShadowList(inline["box-shadow"])[0] : null;
  const transform = inline.transform ? parseTransform(inline.transform) : null;

  if (isTextEl) {
    const text = el.children[0] as string;
    const node = makeTextFromStyle({
      scene,
      x: box.x, y: box.y, w: box.w, h: box.h,
      text,
      style: box.style,
      z,
      tagName: el.tag,
      className: el.attrs.class,
      styleAttr: box.styleAttr,
    });
    if (shadow) {
      node.shadowOffsetX = shadow.offsetX;
      node.shadowOffsetY = shadow.offsetY;
      node.shadowBlur = shadow.blur;
      node.shadowSpread = shadow.spread;
      node.shadowColor = shadow.color;
    }
    if (transform) {
      node.rotation = transform.rotation;
      node.scaleX = transform.scaleX;
      node.scaleY = transform.scaleY;
    }
    addNode(scene, node);
    return node;
  }
  if (el.tag === "img") {
    const node = makeImageFromStyle({
      scene,
      x: box.x, y: box.y, w: box.w, h: box.h,
      imageUrl: el.attrs.src ?? "",
      style: box.style,
      z,
      tagName: el.tag,
      className: el.attrs.class,
      styleAttr: box.styleAttr,
    });
    if (shadow) {
      node.shadowOffsetX = shadow.offsetX;
      node.shadowOffsetY = shadow.offsetY;
      node.shadowBlur = shadow.blur;
      node.shadowSpread = shadow.spread;
      node.shadowColor = shadow.color;
    }
    if (transform) {
      node.rotation = transform.rotation;
      node.scaleX = transform.scaleX;
      node.scaleY = transform.scaleY;
    }
    addNode(scene, node);
    return node;
  }
  // SVG <path>: rasterize the `d` attribute to a texture and attach as a
  // pathD field so the rect pipeline samples it like a text/image.
  if (el.tag === "path" && el.attrs.d) {
    const node = makeRectFromStyle({
      scene,
      x: box.x, y: box.y, w: box.w, h: box.h,
      style: box.style,
      z,
      tagName: el.tag,
      className: el.attrs.class,
      styleAttr: box.styleAttr,
      viewportW,
      viewportH,
    });
    node.pathD = el.attrs.d;
    if (el.attrs.viewBox) {
      const parts = el.attrs.viewBox.trim().split(/[\s,]+/).map(Number);
      if (parts.length === 4 && parts.every(Number.isFinite)) {
        node.pathViewBox = { x: parts[0], y: parts[1], w: parts[2], h: parts[3] };
      }
    }
    addNode(scene, node);
    return node;
  }
  // Background fill?
  if (box.style.fill.a > 0 || (box.style.borderWidths[0] + box.style.borderWidths[1] + box.style.borderWidths[2] + box.style.borderWidths[3] > 0)) {
    const node = makeRectFromStyle({
      scene,
      x: box.x, y: box.y, w: box.w, h: box.h,
      style: box.style,
      z,
      tagName: el.tag,
      className: el.attrs.class,
      styleAttr: box.styleAttr,
      viewportW,
      viewportH,
    });
    if (shadow) {
      node.shadowOffsetX = shadow.offsetX;
      node.shadowOffsetY = shadow.offsetY;
      node.shadowBlur = shadow.blur;
      node.shadowSpread = shadow.spread;
      node.shadowColor = shadow.color;
    }
    if (transform) {
      node.rotation = transform.rotation;
      node.scaleX = transform.scaleX;
      node.scaleY = transform.scaleY;
    }
    addNode(scene, node);
    return node;
  }
  // hr (horizontal rule)
  if (el.tag === "hr") {
    const node = makeRectFromStyle({
      scene,
      x: box.x, y: box.y, w: box.w, h: 1,
      style: box.style,
      z,
      tagName: el.tag,
      className: el.attrs.class,
      styleAttr: box.styleAttr,
      viewportW,
      viewportH,
    });
    addNode(scene, node);
    return node;
  }
  // Background also required when child elements stack on top — render a
  // transparent rect to receive hit-test (skip — leaves pass-through to
  // children).
  return null;
}

function makeRectFromStyle(args: {
  scene: Scene;
  x: number; y: number; w: number; h: number;
  style: ComputedStyle;
  z: number;
  tagName?: string;
  className?: string;
  styleAttr?: string;
  viewportW?: number;
  viewportH?: number;
}): SceneNode {
  // Lazy import to avoid circular dep at top.
  // We import locally so the sceneGraph module doesn't have to know about CSS.
  // makeRect imported at top
  const node = makeRect({
    x: args.x,
    y: args.y,
    w: Math.max(1, args.w),
    h: Math.max(1, args.h),
    radii: resolveCornerRadii(args.style.radii, Math.max(1, args.w), Math.max(1, args.h), args.viewportW ?? 0, args.viewportH ?? 0),
    fill: args.style.fill,
    gradientEnd: args.style.gradientEnd,
    gradientAngle: args.style.gradientAngle,
    imageUrl: args.style.imageUrl,
    borderWidths: args.style.borderWidths,
    borderColor: args.style.borderColor,
    strokeWidth: args.style.borderWidths[0],
    stroke: args.style.borderColor,
    shadowOffsetX: args.style.shadowOffsetX,
    shadowOffsetY: args.style.shadowOffsetY,
    shadowBlur: args.style.shadowBlur,
    shadowSpread: args.style.shadowSpread,
    shadowColor: args.style.shadowColor,
    rotation: args.style.rotation,
    scaleX: args.style.scaleX,
    scaleY: args.style.scaleY,
    transformOriginX: args.style.transformOriginX,
    transformOriginY: args.style.transformOriginY,
    opacity: args.style.opacity,
    mixBlendMode: args.style.mixBlendMode,
    visibility: args.style.visibility,
    pointerEvents: args.style.pointerEvents,
    overflow: args.style.overflow,
    filter: args.style.filter,
    zIndex: args.z,
    tagName: args.tagName,
    className: args.className,
    styleAttr: args.styleAttr,
  });
  return node;
}

function makeTextFromStyle(args: {
  scene: Scene;
  x: number; y: number; w: number; h: number;
  text: string;
  style: ComputedStyle;
  z: number;
  tagName?: string;
  className?: string;
  styleAttr?: string;
}): SceneNode {
  // makeText imported at top
  const node = makeText({
    x: args.x,
    y: args.y,
    w: Math.max(1, args.w),
    h: Math.max(1, args.h),
    text: args.text,
    radii: args.style.radii,
    fill: args.style.fill,
    textColor: args.style.textColor,
    fontSize: args.style.fontSize,
    fontWeight: args.style.fontWeight,
    fontFamily: args.style.fontFamily,
    textWrap: args.style.textWrap,
    lineHeight: args.style.lineHeight,
    letterSpacing: args.style.letterSpacing,
    textAlign: args.style.textAlign,
    textOverflowEllipsis: args.style.textOverflowEllipsis,
    strokeWidth: args.style.borderWidths[0],
    stroke: args.style.borderColor,
    shadowOffsetX: args.style.shadowOffsetX,
    shadowOffsetY: args.style.shadowOffsetY,
    shadowBlur: args.style.shadowBlur,
    shadowSpread: args.style.shadowSpread,
    shadowColor: args.style.shadowColor,
    rotation: args.style.rotation,
    scaleX: args.style.scaleX,
    scaleY: args.style.scaleY,
    transformOriginX: args.style.transformOriginX,
    transformOriginY: args.style.transformOriginY,
    opacity: args.style.opacity,
    mixBlendMode: args.style.mixBlendMode,
    visibility: args.style.visibility,
    pointerEvents: args.style.pointerEvents,
    overflow: args.style.overflow,
    filter: args.style.filter,
    zIndex: args.z,
    tagName: args.tagName,
    className: args.className,
    styleAttr: args.styleAttr,
  });
  return node;
}

function makeImageFromStyle(args: {
  scene: Scene;
  x: number; y: number; w: number; h: number;
  imageUrl: string;
  style: ComputedStyle;
  z: number;
  tagName?: string;
  className?: string;
  styleAttr?: string;
}): SceneNode {
  // makeImage imported at top
  const node = makeImage({
    x: args.x,
    y: args.y,
    w: Math.max(1, args.w),
    h: Math.max(1, args.h),
    imageUrl: args.imageUrl,
    radii: args.style.radii,
    fill: args.style.fill,
    strokeWidth: args.style.borderWidths[0],
    stroke: args.style.borderColor,
    shadowOffsetX: args.style.shadowOffsetX,
    shadowOffsetY: args.style.shadowOffsetY,
    shadowBlur: args.style.shadowBlur,
    shadowSpread: args.style.shadowSpread,
    shadowColor: args.style.shadowColor,
    rotation: args.style.rotation,
    scaleX: args.style.scaleX,
    scaleY: args.style.scaleY,
    transformOriginX: args.style.transformOriginX,
    transformOriginY: args.style.transformOriginY,
    opacity: args.style.opacity,
    mixBlendMode: args.style.mixBlendMode,
    visibility: args.style.visibility,
    pointerEvents: args.style.pointerEvents,
    overflow: args.style.overflow,
    filter: args.style.filter,
    zIndex: args.z,
    tagName: args.tagName,
    className: args.className,
    styleAttr: args.styleAttr,
  });
  return node;
}

// ---------------------------------------------------------------------------
// Public entry: HTML + CSS → Scene
// ---------------------------------------------------------------------------

export interface CompileOptions {
  viewportW: number;
  viewportH: number;
  /** Base font for em/rem. Default 16. */
  baseFontPx?: number;
}

export function compileHtml(html: string, opts: CompileOptions): Scene {
  const scene = createScene();
  const styleBlocks = extractStyleBlocks(html);
  const rules: CssRule[] = [];
  for (const body of styleBlocks) rules.push(...parseCssBody(body));
  const keyframes: Record<string, KeyframeTrack> = parseKeyframes(styleBlocks.join("\n"));

  const elements = parseHtml(html);
  const ctx: ApplyContext = {
    viewportW: opts.viewportW,
    viewportH: opts.viewportH,
    baseFontPx: opts.baseFontPx ?? 16,
    isHovered: false,
    isFocused: false,
    isActive: false,
  };

  const layout = runLayout(elements, null, rules, ctx, null);
  buildSceneFromLayout(layout, scene, 0, ctx.viewportW, ctx.viewportH);

  // Layer 7 (typography) — for text nodes, resize the rect to match the
  // natural text width so the canvas-baked texture (which is sized to the
  // text) aligns 1:1 with the rect. Without this, text rectangles are too
  // small and the canvas UV mapping clips the glyphs.
  //
  // Important: when the layout gave us a rect much wider than the natural
  // text width (common for nodes whose parent stretches them to fill a row),
  // we OVERRIDE the width rather than max(). Otherwise the canvas-bake is
  // stretched across the wide rect and the text becomes unreadable.
  if (typeof document !== "undefined") {
    const probe = document.createElement("canvas").getContext("2d")!;
    for (const n of scene.nodes.values()) {
      if (n.type !== "text" || !n.text) continue;
      const size = (n.fontSize ?? 16) * Math.min(window.devicePixelRatio || 1, 2);
      const weight = n.fontWeight ?? 400;
      const family = n.fontFamily ?? "system-ui, -apple-system, 'SF Pro Display', 'Segoe UI', Roboto, sans-serif";
      const letterSpacing = (n.letterSpacing ?? 0) * Math.min(window.devicePixelRatio || 1, 2);
      probe.font = `${weight} ${size}px ${family}`;
      const m = probe.measureText(n.text);
      const cssW = m.width + letterSpacing * Math.max(0, n.text.length - 1) + 8;
      const cssH = (n.lineHeight ?? 1.2) * (n.fontSize ?? 16) + 6;
      // Use natural text dimensions; the layout pass has no idea what the
      // text will actually measure to, so it almost always over-allocates.
      n.w = cssW;
      n.h = cssH;
      // The canvas-baked texture already has the glyph color baked in, so
      // the underlying rect fill must be transparent — otherwise it paints
      // solid fill over the texture and we see "rect colored" instead of
      // "text colored".
      if (n.textColor && n.textColor.a > 0) {
        n.fill = { r: 1, g: 1, b: 1, a: 0 };
      }
    }
  }

  // Wire keyframes — for each scene node whose className or tag matches a
  // keyframe rule, attach the track.
  for (const n of scene.nodes.values()) {
    const candidates = [
      n.tagName,
      `.${n.className ?? ""}`,
    ].filter(Boolean) as string[];
    for (const c of candidates) {
      for (const [name, track] of Object.entries(keyframes)) {
        if (c.includes(name) || n.className?.split(/\s+/).includes(name)) {
          n.animation = { ...track };
        }
      }
    }
  }

  // Stash rules in scene so the engine can resolve :hover / :focus.
  // Key format: "tag.class1.class2:pseudo" — pseudo suffix disambiguates
  // :hover / :focus / :active variants of the same selector. The empty
  // pseudo "" matches the base state; "hover" matches when the node is
  // hovered, etc.
  for (const r of rules) {
    const sel = compileSelector(r.selector);
    if (!sel) continue;
    const pseudo = sel.pseudo ? `:${sel.pseudo}` : "";
    const key = `${sel.tag ?? ""}.${sel.classes.join(".")}${pseudo}`;
    scene.styleRules.set(key, r.decls as Partial<ComputedStyle>);
  }

  return scene;
}

// ---------------------------------------------------------------------------
// @keyframes parsing
// ---------------------------------------------------------------------------

/**
 * Parse @keyframes from a CSS body. Supports:
 *   @keyframes name {
 *     from { ... }
 *     50% { ... }
 *     to { ... }
 *   }
 *
 * Returns a map of name → KeyframeTrack.
 */
export function parseKeyframes(body: string): Record<string, KeyframeTrack> {
  const out: Record<string, KeyframeTrack> = {};
  const re = /@keyframes\s+([\w-]+)\s*\{([\s\S]*?)\}\s*(?=@keyframes|@|$)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    const name = m[1];
    const body = m[2];
    const keyframes: KeyframeDef[] = [];
    // Each frame is a percentage selector followed by a {} block.
    const frameRe = /([\d.]+%|from|to)\s*(?:,\s*([\d.]+%|from|to))*\s*\{([^}]*)\}/g;
    let f: RegExpExecArray | null;
    while ((f = frameRe.exec(body)) !== null) {
      const selectors = [f[1], f[2]].filter(Boolean) as string[];
      const props = parseStyleAttr(f[3]);
      const animatable = styleDeclToAnimatable(props);
      for (const sel of selectors) {
        keyframes.push({
          at: sel === "from" ? 0 : sel === "to" ? 1 : Number(sel.slice(0, -1)) / 100,
          props: animatable,
        });
      }
    }
    keyframes.sort((a, b) => a.at - b.at);
    out[name] = {
      duration: 1,
      delay: 0,
      iterations: "infinite",
      direction: "normal",
      fillMode: "none",
      keyframes,
      currentIteration: 0,
      elapsedInIteration: 0,
      paused: false,
    };
  }
  return out;
}

function styleDeclToAnimatable(decls: StyleDecl): Partial<KeyframeDef["props"]> {
  const out: Partial<KeyframeDef["props"]> = {};
  for (const [k, v] of Object.entries(decls)) {
    switch (k) {
      case "opacity":
        out.opacity = Number(v);
        break;
      case "transform": {
        const t = parseTransform(v);
        out.rotation = t.rotation;
        out.scaleX = t.scaleX;
        out.scaleY = t.scaleY;
        break;
      }
      case "translate":
      case "translateX":
        // We'd need a separate x channel; PoC folds it into a transform.
        break;
    }
  }
  return out;
}

/** Apply parsed animation shorthand to a track. */
export function applyAnimationShorthand(
  track: KeyframeTrack,
  shorthand: string,
): void {
  // "fade 1s ease-in 0.5s infinite" → split into tokens
  // We don't parse the full shorthand but extract duration, delay, iteration.
  const parts = shorthand.split(/\s+/);
  let timeIdx = 0;
  for (const p of parts) {
    if (/^\d+(\.\d+)?(s|ms)$/i.test(p)) {
      const ms = p.endsWith("ms") ? Number(p.slice(0, -2)) : Number(p.slice(0, -1)) * 1000;
      if (timeIdx === 0) track.duration = ms / 1000;
      else track.delay = ms / 1000;
      timeIdx++;
    } else if (p === "infinite") {
      track.iterations = "infinite";
    } else if (/^\d+$/.test(p)) {
      track.iterations = Number(p);
    } else if (p === "normal" || p === "reverse" || p === "alternate" || p === "alternate-reverse") {
      track.direction = p;
    } else if (p === "none" || p === "forwards" || p === "backwards" || p === "both") {
      track.fillMode = p;
    } else if (/^(linear|ease|ease-in|ease-out|ease-in-out|cubic-bezier)/.test(p)) {
      track.defaultEasing = p;
    }
  }
}

// ---------------------------------------------------------------------------
// CSS Grid (Layer 4)
// ---------------------------------------------------------------------------

interface GridTrack {
  /** px value (negative sentinels for vh/vw from parseLength). */
  px: number;
  /** Unit tag — "px", "%", "fr", or "auto". */
  unit: "px" | "%" | "fr" | "auto";
  /** fr multiplier (0 unless unit === "fr"). */
  fr: number;
  /** % of available (0 unless unit === "%"). */
  pct: number;
}

/**
 * Parse a CSS track-list string like "1fr 2fr 100px" or "auto 1fr" or
 * "repeat(3, 100px)" (repeat with literal count only — repeat(auto-fill) is
 * not supported in Layer 4).
 */
export function parseGridTrackList(value: string): GridTrack[] {
  const out: GridTrack[] = [];
  if (!value || !value.trim()) return out;

  // Expand repeat(N, ...) into N copies of the inner tracks.
  const expandRepeat = (s: string): string => {
    const re = /repeat\(\s*(\d+)\s*,\s*([^)]+)\)/g;
    return s.replace(re, (_m, n: string, inner: string) => {
      const count = Number(n);
      const parts = inner.split(",").map((p) => p.trim());
      return Array(count).fill(parts.join(" ")).join(" ");
    });
  };
  const expanded = expandRepeat(value);

  for (const raw of expanded.trim().split(/\s+/)) {
    const s = raw.trim();
    if (!s) continue;
    if (s === "auto") {
      out.push({ px: NaN, unit: "auto", fr: 0, pct: 0 });
      continue;
    }
    if (s.endsWith("fr")) {
      const fr = Number(s.slice(0, -2));
      if (Number.isFinite(fr)) out.push({ px: NaN, unit: "fr", fr, pct: 0 });
      continue;
    }
    if (s.endsWith("%")) {
      const pct = Number(s.slice(0, -1));
      if (Number.isFinite(pct)) out.push({ px: NaN, unit: "%", fr: 0, pct });
      continue;
    }
    const parsed = parseLength(s);
    out.push({ px: parsed, unit: "px", fr: 0, pct: 0 });
  }
  return out;
}

/**
 * Resolve a list of grid tracks against an available axis length. Distributes
 * remaining space to fr units proportionally. Auto tracks get 0.
 */
export function resolveGridTracks(
  tracks: GridTrack[],
  availablePx: number,
  viewportW: number,
  viewportH: number,
  fontSize: number,
): number[] {
  if (tracks.length === 0) return [];

  let fixedSum = 0;
  let frSum = 0;
  for (const t of tracks) {
    if (t.unit === "px") {
      const v = resolveLength(t.px, viewportW, viewportH, fontSize);
      fixedSum += Number.isFinite(v) ? v : 0;
    } else if (t.unit === "%") {
      fixedSum += (availablePx * t.pct) / 100;
    } else if (t.unit === "fr") {
      frSum += t.fr;
    }
  }
  const frSpace = Math.max(0, availablePx - fixedSum);
  const frUnit = frSum > 0 ? frSpace / frSum : 0;

  const out: number[] = [];
  for (const t of tracks) {
    if (t.unit === "px") {
      const v = resolveLength(t.px, viewportW, viewportH, fontSize);
      out.push(Number.isFinite(v) ? v : 0);
    } else if (t.unit === "%") {
      out.push((availablePx * t.pct) / 100);
    } else if (t.unit === "fr") {
      out.push(frUnit * t.fr);
    } else {
      out.push(0);
    }
  }
  return out;
}

/**
 * Place children into a CSS Grid in row-major order.
 * Children with explicit width/height keep them (left/top-aligned within cell).
 * Auto-sized children take the full cell.
 */
function layoutGridChildren(
  el: HtmlElement,
  box: LayoutBox,
  rules: CssRule[],
  ctx: ApplyContext,
  style: ComputedStyle,
): LayoutBox[] {
  const innerW = box.w - box.padding[1] - box.padding[3];
  const innerH = box.h - box.padding[0] - box.padding[2];
  const kids = el.children.filter((c): c is HtmlElement => typeof c !== "string");
  const childBoxes: LayoutBox[] = [];

  for (const k of kids) {
    const childStyle = resolveStyle(k, rules, ctx, style);
    if (childStyle.display === "none") continue;
    const childInline = collectInlineDecls(k);
    const childMargin = childInline.margin
      ? parseBoxShorthand(childInline.margin, ctx)
      : ([0, 0, 0, 0] as BorderWidths);
    const childPad = childInline.padding
      ? parseBoxShorthand(childInline.padding, ctx)
      : ([0, 0, 0, 0] as BorderWidths);

    const hasChildW = !!(childInline.width || childInline["max-width"]);
    const hasChildH = !!(childInline.height || childInline["max-height"]);
    const cw = hasChildW
      ? resolveBoxSize(childInline.width, childInline["max-width"], innerW, ctx, childStyle.fontSize)
      : 0;
    const ch = hasChildH
      ? resolveBoxSize(childInline.height, childInline["max-height"], innerH, ctx, childStyle.fontSize)
      : 0;
    const cb: LayoutBox = {
      el: k,
      style: childStyle,
      x: 0, y: 0,
      w: cw,
      h: ch,
      hasExplicitW: hasChildW,
      hasExplicitH: hasChildH,
      margin: childMargin,
      padding: childPad,
      children: [],
      styleAttr: k.attrs.style ?? "",
      matchingDecls: collectMatchingDecls(k, rules, ctx),
    };
    childBoxes.push(cb);
  }

  if (childBoxes.length === 0) return childBoxes;

  const cols = parseGridTrackList(style.gridTemplateColumns);
  const rows = parseGridTrackList(style.gridTemplateRows);
  const gap = style.gap;

  const colCount = cols.length > 0 ? cols.length : 1;
  const rowCount = rows.length > 0
    ? rows.length
    : Math.ceil(childBoxes.length / colCount);

  const colGapTotal = gap * Math.max(0, colCount - 1);
  const rowGapTotal = gap * Math.max(0, rowCount - 1);
  const colSizes = resolveGridTracks(
    cols.length > 0 ? cols : [{ px: innerW - colGapTotal, unit: "px", fr: 0, pct: 0 }],
    innerW - colGapTotal,
    ctx.viewportW,
    ctx.viewportH,
    style.fontSize,
  );
  const rowSizes = resolveGridTracks(
    rows.length > 0 ? rows : [{ px: innerH - rowGapTotal, unit: "px", fr: 0, pct: 0 }],
    innerH - rowGapTotal,
    ctx.viewportW,
    ctx.viewportH,
    style.fontSize,
  );

  const colOffsets: number[] = [];
  let cursor = 0;
  for (let i = 0; i < colCount; i++) {
    colOffsets.push(cursor);
    cursor += colSizes[i] ?? 0;
    if (i < colCount - 1) cursor += gap;
  }
  const rowOffsets: number[] = [];
  let rowCursor = 0;
  for (let i = 0; i < rowCount; i++) {
    rowOffsets.push(rowCursor);
    rowCursor += rowSizes[i] ?? 0;
    if (i < rowCount - 1) rowCursor += gap;
  }

  for (let i = 0; i < childBoxes.length; i++) {
    const c = childBoxes[i];
    const col = i % colCount;
    const row = Math.floor(i / colCount);
    if (row >= rowCount) break;
    c.x = box.x + (colOffsets[col] ?? 0) + box.padding[3];
    c.y = box.y + (rowOffsets[row] ?? 0) + box.padding[0];
    const cellW = colSizes[col] ?? 0;
    const cellH = rowSizes[row] ?? 0;
    if (!(c.w > 0)) c.w = cellW;
    if (!(c.h > 0)) c.h = cellH;
  }

  // Recompute container height to fit rows when no explicit height.
  if (!box.hasExplicitH) {
    const totalRowH =
      rowSizes.reduce((s, r) => s + r, 0) +
      Math.max(0, rowCount - 1) * gap;
    box.h = totalRowH + box.padding[0] + box.padding[2];
  }

  return childBoxes;
}